<template>
  <div
    class="kiosk-page w-full flex flex-col"
  >
    <hosted-page-banner
      :brand-name="brandName"
      :brand-colour="brandColour"
      :icon-path="iconPath"
      :logo-path="logoPath"
      :prefer-logo-over-icon="preferLogoOverIcon"
    />

    <div class="w-full bg-white flex justify-center flex-grow items-start md:items-center p-4 md:p-base md:pt-8">
      <transition :name="transitionName" mode="out-in">
        <kiosk-welcome
          v-if="step === 'welcome'"
          key="welcome"
          :brand-name="brandName"
          :accent-colour="accentColour"
          :people-waiting="peopleWaiting"
          :open-day="openDay"
          :waitlist-form-url="waitlistFormUrl"
          @join="goToForm"
        />

        <kiosk-join-form
          v-else-if="step === 'form'"
          key="form"
          :accent-colour="accentColour"
          :tenant-url-links-suffix="tenantUrlLinksSuffix"
          :policy-type="policyType"
          :link-policy="linkPolicy"
          :waitlist-form-url="waitlistFormUrl"
          @back="goToWelcome"
          @booked="handleBooked"
        />

        <kiosk-confirmation
          v-else-if="step === 'confirmation'"
          key="confirmation"
          :accent-colour="accentColour"
          :turn-info="turnInfo"
          :customer-name="customerName"
          :customer-phone="customerPhone"
          :customer-email="customerEmail"
          :prefix-queue-position="prefixQueuePosition"
          @return="handleReturn"
        />
      </transition>
    </div>

    <powered-by-footer page-type="waiting-list" />
  </div>
</template>

<script>
import HostedPageBanner from '@/components/HostedPageBanner.vue';
import PoweredByFooter from '@/views/pages/_components/PoweredByFooter.vue';
import KioskWelcome from './KioskWelcome.vue';
import KioskJoinForm from './KioskJoinForm.vue';
import KioskConfirmation from './KioskConfirmation.vue';
import waitingListService from '@/api/modules/waiting-list.service';
import themeConfig from '../../../../themeConfig';

export default {
  name: 'KioskPage',
  components: {
    HostedPageBanner,
    PoweredByFooter,
    KioskWelcome,
    KioskJoinForm,
    KioskConfirmation,
  },
  data() {
    return {
      step: 'welcome',
      transitionName: 'kiosk-slide-left',

      // Branding
      brandName: 'Company Name',
      brandColour: themeConfig.colors.primary,
      accentColour: themeConfig.colors.primary,
      iconPath: '',
      logoPath: '',
      preferLogoOverIcon: false,

      // Policy
      policyType: '',
      linkPolicy: '',

      // Queue state
      openDay: true,
      prefixQueuePosition: '',
      peopleWaiting: 0,
      tenantUrlLinksSuffix: '',

      // Turn info after booking
      turnInfo: null,
      customerName: '',
      customerPhone: '',
      customerEmail: '',

      // Polling
      brandingPollInterval: null,
      BRANDING_POLL_MS: 30000,
    };
  },
  computed: {
    waitlistFormUrl() {
      const dashboardUrl = this.$enums.AppLinks.DASHBOARD;
      const baseUrl = dashboardUrl.endsWith('/') ? dashboardUrl : `${dashboardUrl}/`;
      return `${baseUrl}waitlist/${this.tenantUrlLinksSuffix}`;
    },
  },
  async created() {
    this.addNoIndexMetaTags();

    const { tenantUrlLinksSuffix } = this.$route.params;
    if (tenantUrlLinksSuffix) {
      this.tenantUrlLinksSuffix = tenantUrlLinksSuffix;
      await this.fetchBranding(tenantUrlLinksSuffix);
      this.startBrandingPoll();
    } else {
      this.$router.push({ name: 'page-error-404' });
    }
  },
  beforeDestroy() {
    this.stopBrandingPoll();
  },
  methods: {
    goToForm() {
      this.transitionName = 'kiosk-slide-left';
      this.step = 'form';
    },
    goToWelcome() {
      this.transitionName = 'kiosk-slide-right';
      this.step = 'welcome';
    },
    handleBooked({ turnInfo, customerName, customerPhone, customerEmail }) {
      this.turnInfo = turnInfo;
      this.customerName = customerName;
      this.customerPhone = customerPhone;
      this.customerEmail = customerEmail;
      this.transitionName = 'kiosk-slide-left';
      this.step = 'confirmation';
    },
    async handleReturn() {
      this.turnInfo = null;
      this.customerName = '';
      this.customerPhone = '';
      this.customerEmail = '';
      this.transitionName = 'kiosk-slide-right';
      this.step = 'welcome';
      await this.fetchBranding(this.tenantUrlLinksSuffix);
    },
    startBrandingPoll() {
      this.stopBrandingPoll();
      this.brandingPollInterval = setInterval(() => {
        if (this.step === 'welcome' && !document.hidden) {
          this.fetchBranding(this.tenantUrlLinksSuffix);
        }
      }, this.BRANDING_POLL_MS);
    },
    stopBrandingPoll() {
      if (this.brandingPollInterval) {
        clearInterval(this.brandingPollInterval);
        this.brandingPollInterval = null;
      }
    },
    async fetchBranding(tenantUrlLinksSuffix) {
      try {
        const resp = await waitingListService.getWaitingListBrandingDetails(tenantUrlLinksSuffix);
        const {
          name,
          icon,
          logo,
          preferLogoOverIcon,
          brandColour,
          accentColor,
          policyType,
          linkPolicy,
          openDay,
          prefixQueuePosition,
          peopleWaiting,
        } = resp.data;

        this.brandName = name || this.brandName;
        this.iconPath = icon;
        this.logoPath = logo;
        this.preferLogoOverIcon = preferLogoOverIcon;
        this.brandColour = brandColour;
        this.accentColour = accentColor;
        this.policyType = policyType;
        this.linkPolicy = linkPolicy;
        this.openDay = openDay;
        this.prefixQueuePosition = prefixQueuePosition;
        this.peopleWaiting = peopleWaiting || 0;

        document.title = `Kiosk | ${this.brandName}`;
      } catch (err) {
        this.$router.push({ name: 'page-error-404' });
      }
    },
    addNoIndexMetaTags() {
      const existingRobotsTag = document.querySelector('meta[name="robots"]');
      const existingGooglebotTag = document.querySelector('meta[name="googlebot"]');
      if (existingRobotsTag) existingRobotsTag.remove();
      if (existingGooglebotTag) existingGooglebotTag.remove();

      const robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      robotsTag.content = 'noindex, nofollow';
      document.head.appendChild(robotsTag);

      const googlebotTag = document.createElement('meta');
      googlebotTag.name = 'googlebot';
      googlebotTag.content = 'noindex, nofollow';
      document.head.appendChild(googlebotTag);
    },
  },
};
</script>

<style lang="scss">
.kiosk-page {
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  position: relative;

  @supports (-webkit-touch-callout: none) {
    min-height: -webkit-fill-available;
  }
}

/* Step transition animations */
.kiosk-slide-left-enter-active,
.kiosk-slide-left-leave-active,
.kiosk-slide-right-enter-active,
.kiosk-slide-right-leave-active {
  transition: all 0.3s ease;
}

.kiosk-slide-left-enter {
  transform: translateX(30px);
  opacity: 0;
}
.kiosk-slide-left-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

.kiosk-slide-right-enter {
  transform: translateX(-30px);
  opacity: 0;
}
.kiosk-slide-right-leave-to {
  transform: translateX(30px);
  opacity: 0;
}
</style>
